export const HWCG_TOUR_PARAM = 'hwcgTour';

export type HwcgTourPhase = 'library' | 'wizard' | 'roster' | 'tasks';

export const HWCG_TOUR_WIZARD_HREF = `/create/incident?${HWCG_TOUR_PARAM}=1`;

/** Opens create incident and starts the HWCG Planning & Execution walkthrough. */
export const HWCG_TOUR_HREF = HWCG_TOUR_WIZARD_HREF;

export function hwcgTourRosterHref(incidentId: string): string {
  return `/iap/${incidentId}/roster?${HWCG_TOUR_PARAM}=roster`;
}

export function hwcgTourTasksHref(incidentId: string): string {
  return `/iap/${incidentId}/actions?${HWCG_TOUR_PARAM}=1`;
}

function firstLiveIncidentId(
  currentId: string | undefined,
  incidents: readonly { id: string; archived?: number | boolean }[]
): string | null {
  if (currentId) return currentId;
  const live = incidents.find((incident) => !incident.archived);
  return live ? String(live.id) : null;
}

export function resolveHwcgTourRosterHref(
  currentId?: string,
  incidents: readonly { id: string; archived?: number | boolean }[] = []
): string | null {
  const id = firstLiveIncidentId(currentId, incidents);
  return id ? hwcgTourRosterHref(id) : null;
}

export function resolveHwcgTourTasksHref(
  currentId?: string,
  incidents: readonly { id: string; archived?: number | boolean }[] = []
): string | null {
  const id = firstLiveIncidentId(currentId, incidents);
  return id ? hwcgTourTasksHref(id) : null;
}

export function wantsHwcgTour(
  param: string | null,
  phase: HwcgTourPhase
): boolean {
  return param === '1' || param === phase;
}

/** Product Tours nav lists this tour only when the HWCG flag is on. */
export function hwcgTourNavVisible(hwcg: boolean): boolean {
  return hwcg;
}

export const HWCG_TOUR_PHASE_LABELS: Record<HwcgTourPhase, string> = {
  library: 'Plan Library',
  wizard: 'Create',
  roster: 'Roster',
  tasks: 'Tasks',
};

export type HwcgTourStepId =
  | 'intro'
  | 'newPlan'
  | 'addTask'
  | 'incidentSpecifics'
  | 'applyOnCreate'
  | 'rosterPlanning'
  | 'rosterViews'
  | 'rosterExecution'
  | 'rosterApply'
  | 'tasksApply'
  | 'tasksLayout'
  | 'tasksAssignees'
  | 'taskRecord'
  | 'stickMenu';

export type HwcgTourStepDef = {
  id: HwcgTourStepId;
  phase: HwcgTourPhase;
  title: string;
  description: string;
  tour?: string;
  selector?: string;
  /** How long to wait for a target that may be absent. */
  waitMs?: number;
};

export const HWCG_TOUR_STEPS: HwcgTourStepDef[] = [
  {
    id: 'intro',
    phase: 'library',
    tour: 'hwcg-plan-library',
    title: 'HWCG Planning & Execution',
    description:
      'Build a named plan of template tasks and ICS forms here, then pull that plan into a workspace. You can do that while creating an incident, from a roster position, or from Tasks. A task stays on Planning while its status is Planned. Current/Ongoing, Blocked, Submitted for Review, and Approved move it to Execution. Closed leaves both.',
  },
  {
    id: 'newPlan',
    phase: 'library',
    tour: 'hwcg-plan-new',
    title: 'Create a plan',
    description:
      'New Plan saves a named plan for this organization. The tasks and ICS forms you add next are what a workspace will create.',
  },
  {
    id: 'addTask',
    phase: 'library',
    tour: 'hwcg-plan-add-task',
    title: 'Add a template task',
    description:
      'Open a plan and add a template task or an ICS form. Set a task’s ICS Position to the seat that should own the work, and leave the status Planned if it should start on the Planning tab. With an empty library, create the plan first — tasks and forms are added inside it.',
  },
  {
    id: 'incidentSpecifics',
    phase: 'wizard',
    tour: 'hwcg-group-actions',
    title: 'Capture the call',
    description:
      'The required call fields, the authorized individual, and a group action are filled in here. Each group is set to Activate, Notify, or No Action.',
  },
  {
    id: 'applyOnCreate',
    phase: 'wizard',
    tour: 'hwcg-create-apply-plan',
    title: 'Pull a plan in while creating',
    description:
      'A plan is required. Plans for this responsible party (and untagged plans) appear here. A filled roster on the plan seats people in the draft below; tasks and ICS forms are created after the workspace exists.',
  },
  {
    id: 'rosterPlanning',
    phase: 'roster',
    tour: 'hwcg-roster-planning',
    title: 'Planning stays Planned',
    waitMs: 500,
    description:
      'Planning Phase lists this seat’s tasks whose status is Planned.',
  },
  {
    id: 'rosterViews',
    phase: 'roster',
    tour: 'hwcg-tasks-view',
    title: 'List, board, or timeline',
    description:
      'List, Board, and Timeline are in this menu. On List, Sub-Task Completion is its own column. Add Task creates the task immediately and assigns it to this seat.',
    waitMs: 500,
  },
  {
    id: 'rosterExecution',
    phase: 'roster',
    tour: 'hwcg-roster-execution',
    title: 'Status moves the task',
    waitMs: 500,
    description:
      'Change the status to Current/Ongoing, Blocked, Submitted for Review, or Approved and the task moves to Execution. Closed leaves both tabs.',
  },
  {
    id: 'rosterApply',
    phase: 'roster',
    tour: 'hwcg-roster-apply-plan',
    title: 'Assign a plan to this position',
    waitMs: 500,
    description:
      'Apply Plan here creates every task in the plan and assigns each one to this position, so the work shows up on this seat. ICS forms on the plan are added to the workspace as well.',
  },
  {
    id: 'tasksApply',
    phase: 'tasks',
    tour: 'hwcg-tasks-apply-plan',
    title: 'Tasks can pull in a plan too',
    description:
      'From Tasks, Apply Plan creates the plan’s tasks and ICS forms in this workspace. Tasks are assigned to the ICS positions saved on each task.',
  },
  {
    id: 'tasksLayout',
    phase: 'tasks',
    tour: 'hwcg-tasks-view',
    title: 'Choose how tasks are shown',
    description:
      'List, Board, and Timeline are in this menu. On List, Sub-Task Completion is its own column.',
  },
  {
    id: 'tasksAssignees',
    phase: 'tasks',
    tour: 'hwcg-assignee-filter',
    title: 'Filter by person or position',
    description:
      'My Tasks limits the list to you. This filter limits it to one person or one roster position.',
  },
  {
    id: 'taskRecord',
    phase: 'tasks',
    tour: 'hwcg-task-record',
    title: 'Inside a task',
    description:
      'The bar shows who last saved this task. Submit for Approval sends it for review, and that control then reads Approve. Sub-Tasks sit above Assets Requested. An asset keeps its catalog name, and Request opens the ICS 213RR. Final Deliverable Attachments can include folders.',
    waitMs: 500,
  },
  {
    id: 'stickMenu',
    phase: 'tasks',
    tour: 'hwcg-stick',
    title: 'Stick keeps the menu open',
    description:
      'Stick keeps this menu open beside the page. With Stick on, the menu starts below the Limited Preview Environment banner.',
    waitMs: 800,
  },
];

export function hwcgTourStepsForPhase(phase: HwcgTourPhase): HwcgTourStepDef[] {
  return HWCG_TOUR_STEPS.filter((step) => step.phase === phase);
}

export function hwcgTourStepsForPhaseIfEnabled(
  phase: HwcgTourPhase,
  hwcgEnabled: boolean
): HwcgTourStepDef[] {
  if (!hwcgEnabled) return [];
  return hwcgTourStepsForPhase(phase);
}

export function hwcgTourStepIds(): HwcgTourStepId[] {
  return HWCG_TOUR_STEPS.map((step) => step.id);
}

export function hwcgTourStepTitles(): string[] {
  return HWCG_TOUR_STEPS.map((step) => step.title);
}
